import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api-client.js';
import { Button } from '../components/ui/button.js';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/card.js';
import { Badge } from '../components/ui/badge.js';
import { EmptyState } from '../components/ui/empty-state.js';
import { FormBanner } from '../components/ui/form.js';

interface ServiceProfileRow {
  id: string;
  slug: string;
  name: string;
  slot_duration_minutes: number;
  status: 'active' | 'disabled';
  auto_confirm: boolean;
  has_browse_password: boolean;
  notification_emails: string[];
  email_account_id: string | null;
  updated_at: string;
}

interface EmbedSnippets {
  iframe: string;
  inline: string;
}

const PATH_PREFIX = (import.meta.env.VITE_BASE_PATH as string | undefined) ?? '';
const PATH_PREFIX_NORMALIZED = PATH_PREFIX.replace(/\/$/, '');

export function ServiceProfilesPage(): JSX.Element {
  const qc = useQueryClient();
  const [embedFor, setEmbedFor] = useState<ServiceProfileRow | null>(null);

  const q = useQuery({
    queryKey: ['admin', 'service-profiles'],
    queryFn: () =>
      api.get('/v1/admin/service-profiles') as Promise<{ data: ServiceProfileRow[] }>,
  });

  const del = useMutation({
    mutationFn: (id: string) => api.delete(`/v1/admin/service-profiles/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'service-profiles'] }),
  });

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Booking pages</h1>
          <p className="text-sm text-ink-muted">
            Public self-service booking. Each profile has a unique slug and an embeddable widget.
          </p>
        </div>
        <Link to="/service-profiles/new">
          <Button>New booking page</Button>
        </Link>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>All booking pages</CardTitle>
          {q.data && <span className="text-xs text-ink-muted">{q.data.data.length}</span>}
        </CardHeader>
        <CardBody className="p-0">
          {q.isLoading && <p className="px-4 py-3 text-sm text-ink-muted">Loading…</p>}
          {q.error && (
            <p className="px-4 py-3 text-sm text-danger">
              {q.error instanceof ApiError && q.error.status === 401
                ? 'Sign in as an admin to manage booking pages.'
                : (q.error as Error).message}
            </p>
          )}
          {q.data && q.data.data.length === 0 && (
            <EmptyState
              title="No booking pages yet"
              description="Create one to publish a /booking/<slug> URL visitors can use to book themselves in."
            />
          )}
          {q.data && q.data.data.length > 0 && (
            <ul className="divide-y divide-border">
              {q.data.data.map((p) => (
                <li
                  key={p.id}
                  className="px-4 py-3 grid grid-cols-[1fr_auto] gap-3 items-center"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Link to={`/service-profiles/${p.id}/edit`} className="font-medium hover:underline truncate">
                        {p.name}
                      </Link>
                      <Badge tone={p.status === 'active' ? 'success' : 'neutral'}>{p.status}</Badge>
                      {p.auto_confirm ? (
                        <Badge tone="info">auto-confirm</Badge>
                      ) : (
                        <Badge tone="warning">requires approval</Badge>
                      )}
                      {p.has_browse_password && <Badge tone="warning">password</Badge>}
                    </div>
                    <p className="text-xs text-ink-muted mt-0.5 font-mono truncate">
                      /{p.slug} · {p.slot_duration_minutes}min slots
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <a
                      href={`${PATH_PREFIX_NORMALIZED}/booking/${p.slug}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Button variant="ghost" size="sm">Preview</Button>
                    </a>
                    <Button variant="ghost" size="sm" onClick={() => setEmbedFor(p)}>
                      Embed
                    </Button>
                    <Link to={`/service-profiles/${p.id}/edit`}>
                      <Button variant="ghost" size="sm">Edit</Button>
                    </Link>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (window.confirm(`Delete booking page "${p.name}"?`)) del.mutate(p.id);
                      }}
                      disabled={del.isPending}
                    >
                      Delete
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      {embedFor && <EmbedModal profile={embedFor} onClose={() => setEmbedFor(null)} />}
    </div>
  );
}

function EmbedModal({
  profile,
  onClose,
}: {
  profile: ServiceProfileRow;
  onClose: () => void;
}): JSX.Element {
  const q = useQuery({
    queryKey: ['admin', 'service-profile-embed', profile.id],
    queryFn: () =>
      api.get(`/v1/admin/service-profiles/${profile.id}/embed-snippets`) as Promise<EmbedSnippets>,
  });

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <div
        className="bg-surface border border-border rounded-lg max-w-2xl w-full max-h-[80vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Embed snippets</h2>
            <p className="text-xs text-ink-muted">{profile.name} · /{profile.slug}</p>
          </div>
          <button onClick={onClose} className="text-ink-muted hover:text-ink text-xl leading-none">
            ×
          </button>
        </div>
        <div className="p-5 space-y-5">
          {q.isLoading && <p className="text-sm text-ink-muted">Loading…</p>}
          {q.error && <FormBanner message={(q.error as Error).message} />}
          {q.data && (
            <>
              <SnippetBlock label="iframe (quickest)" value={q.data.iframe} />
              <SnippetBlock label="inline script" value={q.data.inline} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SnippetBlock({ label, value }: { label: string; value: string }): JSX.Element {
  const copy = (): void => { void navigator.clipboard.writeText(value); };
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-medium">{label}</h3>
        <Button variant="ghost" size="sm" onClick={copy}>Copy</Button>
      </div>
      <pre className="text-xs bg-surface-alt border border-border rounded p-3 overflow-x-auto whitespace-pre-wrap break-all">
        <code>{value}</code>
      </pre>
    </div>
  );
}
